import { Assert } from '@cotera/utilities';
import {
  WORKFLOW_FILTER_COMBINATORS,
  WORKFLOW_FILTER_OPERATORS,
} from '@cotera/api';
import {
  isAddressableUpstreamFieldPath,
  isNodeScopedFieldPath,
  normalizeUpstreamFieldPath,
  resolveUpstreamFieldValue,
} from './upstream-field-path';

/**
 * Operators a filter node can apply to a field of its upstream output. Defined
 * with the node data schemas because the strings are persisted.
 */
export const FILTER_OPERATORS = WORKFLOW_FILTER_OPERATORS;

export type FilterOperator = (typeof FILTER_OPERATORS)[number];

/** Operators that compare against a configured `value`; the rest only test presence. */
export const FILTER_OPERATORS_REQUIRING_VALUE: ReadonlySet<FilterOperator> =
  new Set<FilterOperator>([
    'equals',
    'not-equals',
    'contains',
    'not-contains',
    'greater-than',
    'less-than',
    'greater-than-or-equal',
    'less-than-or-equal',
    'one-of',
    'not-one-of',
  ]);

/**
 * Ordering operators. Their value must be comparable — either a number or a
 * parseable date — so `greater-than "2025-11-01"` works as well as `> 5`.
 */
export const FILTER_OPERATORS_REQUIRING_COMPARABLE: ReadonlySet<FilterOperator> =
  new Set<FilterOperator>([
    'greater-than',
    'less-than',
    'greater-than-or-equal',
    'less-than-or-equal',
  ]);

/** Operators whose value is a list (comma-separated or a JSON array). */
export const FILTER_OPERATORS_REQUIRING_LIST: ReadonlySet<FilterOperator> =
  new Set<FilterOperator>(['one-of', 'not-one-of']);

/** True when a string parses as a finite number. */
export function isNumericFilterValue(value: string): boolean {
  const trimmed = value.trim();
  return trimmed !== '' && !Number.isNaN(Number(trimmed));
}

/**
 * True when a string is usable by the ordering operators — a finite number or a
 * date the runtime can parse (so ISO timestamps and dates are comparable).
 */
export function isComparableFilterValue(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === '') {
    return false;
  }
  return isNumericFilterValue(trimmed) || !Number.isNaN(Date.parse(trimmed));
}

export function isFilterOperator(value: unknown): value is FilterOperator {
  return (
    typeof value === 'string' &&
    (FILTER_OPERATORS as readonly string[]).includes(value)
  );
}

/**
 * Plain-language labels for the operator dropdown. Written to read naturally in a
 * sentence (`/status is "solved"`, `/channel is one of "web, email"`) so the
 * dropdown, the canvas summary, and the run view all stay legible.
 */
export const FILTER_OPERATOR_LABELS: Record<FilterOperator, string> = {
  equals: 'is',
  'not-equals': 'is not',
  contains: 'contains',
  'not-contains': "doesn't contain",
  exists: 'exists',
  'not-exists': "doesn't exist",
  'greater-than': 'is greater than',
  'less-than': 'is less than',
  'greater-than-or-equal': 'is at least',
  'less-than-or-equal': 'is at most',
  'one-of': 'is one of',
  'not-one-of': 'is not one of',
};

/** A single field/operator/value predicate. */
export type FilterCondition = {
  /**
   * The field to test. Either an RFC 6901 JSON Pointer into the direct upstream
   * output (`/field`, `''` = whole value), or an upstream reference to any
   * earlier node's output (`nodeId/pointer`, keyed by the source node's
   * stable id) resolved against the ancestor outputs — mirroring tool-call
   * param bindings.
   */
  readonly path: string;
  readonly operator: FilterOperator;
  /** Comparison value (string form, coerced per operator). Absent for exists/not-exists. */
  readonly value?: string;
};

/**
 * True when a filter field is a node-scoped reference (`nodeId/pointer`) rather
 * than a bare JSON Pointer — i.e. it names an upstream node (by id) to read from.
 *
 * Filter-facing name for the shared {@link isNodeScopedFieldPath}; kept because
 * it is part of the SDK's published surface.
 */
export const isNodeScopedFilterPath = isNodeScopedFieldPath;

/** How conditions/groups combine: `and` (all must pass) or `or` (any passes). */
export const FILTER_COMBINATORS = WORKFLOW_FILTER_COMBINATORS;
export type FilterCombinator = (typeof FILTER_COMBINATORS)[number];

export function isFilterCombinator(value: unknown): value is FilterCombinator {
  return value === 'and' || value === 'or';
}

function readCombinator(
  value: unknown,
  fallback: FilterCombinator
): FilterCombinator {
  return isFilterCombinator(value) ? value : fallback;
}

/** A group of conditions combined by the group's `combinator` (defaults to AND). */
export type FilterGroup = {
  readonly combinator: FilterCombinator;
  readonly conditions: readonly FilterCondition[];
};

/**
 * A filter node's config: a list of groups combined by the top-level
 * `combinator`, each group combining its conditions by its own `combinator`.
 * Two levels of AND/OR express any two-level formula, e.g. `(x OR y) AND (w OR z)`
 * (top = AND, each group = OR) or `(a AND b) OR (c AND d)` (top = OR, groups = AND).
 */
export type FilterConfig = {
  readonly combinator: FilterCombinator;
  readonly groups: readonly FilterGroup[];
};

export type ParseFilterConfigResult =
  | { readonly ok: true; readonly config: FilterConfig }
  | { readonly ok: false; readonly error: string };

type ParseFilterConditionResult =
  | { readonly ok: true; readonly condition: FilterCondition }
  | { readonly ok: false; readonly error: string };

type ParseConditionListResult =
  | { readonly ok: true; readonly conditions: FilterCondition[] }
  | { readonly ok: false; readonly error: string };

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

/**
 * Normalizes a filter field into a bare JSON pointer. The editor stores the field
 * as a `{{/pointer}}` token when it's inserted via the "/"-command upstream picker
 * (reusing the tool-call mechanism), or as a plainly-typed pointer; both resolve
 * to the same bare pointer here. A blank field stays blank, so "blank = whole
 * value" always holds.
 */
export const filterFieldPointer = normalizeUpstreamFieldPath;

/** Validates one field/operator/value predicate. `prefix` labels which row failed. */
function parseFilterCondition(
  data: unknown,
  prefix: string
): ParseFilterConditionResult {
  const record = asRecord(data);
  if (record === null) {
    return { ok: false, error: `${prefix}condition is missing configuration.` };
  }

  const operator = record['operator'];
  if (operator === undefined || operator === null || operator === '') {
    return { ok: false, error: `${prefix}condition is missing an operator.` };
  }
  if (!isFilterOperator(operator)) {
    return {
      ok: false,
      error: `${prefix}condition has an unknown operator: ${String(operator)}`,
    };
  }

  const rawPath = record['path'];
  if (rawPath !== undefined && typeof rawPath !== 'string') {
    return { ok: false, error: `${prefix}condition path must be a string.` };
  }
  // Fields may be stored as a `{{ref}}` token (from the "/" picker); unwrap to
  // the bare pointer or node-scoped ref so the rest of the pipeline sees a plain
  // string.
  const path = filterFieldPointer(rawPath ?? '');
  if (!isAddressableUpstreamFieldPath(path)) {
    return {
      ok: false,
      error: `${prefix}condition path must be a JSON Pointer ("/field") or an upstream reference ("Node/field") (got "${path}").`,
    };
  }

  const rawValue = record['value'];
  let value: string | undefined;
  if (rawValue !== undefined && rawValue !== null) {
    if (typeof rawValue === 'string') {
      value = rawValue;
    } else if (typeof rawValue === 'number' || typeof rawValue === 'boolean') {
      value = String(rawValue);
    } else {
      return { ok: false, error: `${prefix}condition value must be a scalar.` };
    }
  }

  if (
    FILTER_OPERATORS_REQUIRING_VALUE.has(operator) &&
    (value === undefined || value === '')
  ) {
    return {
      ok: false,
      error: `${prefix}operator "${FILTER_OPERATOR_LABELS[operator]}" requires a value.`,
    };
  }

  if (
    FILTER_OPERATORS_REQUIRING_COMPARABLE.has(operator) &&
    (value === undefined || !isComparableFilterValue(value))
  ) {
    return {
      ok: false,
      error: `${prefix}operator "${FILTER_OPERATOR_LABELS[operator]}" requires a number or date.`,
    };
  }

  return { ok: true, condition: { path, operator, value } };
}

/** Validates a list of conditions. `prefix` labels which group failed. */
function parseConditionList(
  rawConditions: unknown,
  prefix: string
): ParseConditionListResult {
  if (!Array.isArray(rawConditions)) {
    return { ok: false, error: `${prefix}conditions must be a list.` };
  }
  if (rawConditions.length === 0) {
    return { ok: false, error: `${prefix}needs at least one condition.` };
  }
  const conditions: FilterCondition[] = [];
  for (let i = 0; i < rawConditions.length; i++) {
    const parsed = parseFilterCondition(
      rawConditions[i],
      `${prefix}condition ${i + 1}: `
    );
    if (!parsed.ok) {
      return { ok: false, error: parsed.error };
    }
    conditions.push(parsed.condition);
  }
  return { ok: true, conditions };
}

/**
 * Validates persisted filter node `data` into a usable {@link FilterConfig}, or
 * returns a clear error so callers can fail the step safely rather than guessing.
 *
 * Accepts three shapes so nodes of every era keep working without a migration:
 *  - current groups shape: `{ combinator, groups: [{ combinator, conditions }] }`
 *  - multi-condition shape: `{ conditions: [...] }` (wrapped into one AND group)
 *  - legacy single shape:   `{ path, operator, value }` (wrapped into one AND group)
 *
 * Missing combinators default to `or` between groups and `and` within a group,
 * which is the behavior earlier group nodes were saved with.
 */
export function parseFilterConfig(data: unknown): ParseFilterConfigResult {
  const record = asRecord(data);
  if (record === null) {
    return { ok: false, error: 'Filter node is missing configuration.' };
  }

  const rawGroups = record['groups'];
  if (rawGroups !== undefined && rawGroups !== null) {
    if (!Array.isArray(rawGroups)) {
      return { ok: false, error: 'Filter node groups must be a list.' };
    }
    if (rawGroups.length === 0) {
      return { ok: false, error: 'Filter node needs at least one group.' };
    }
    const groups: FilterGroup[] = [];
    for (let i = 0; i < rawGroups.length; i++) {
      const groupRecord = asRecord(rawGroups[i]);
      if (groupRecord === null) {
        return { ok: false, error: `Group ${i + 1}: must be an object.` };
      }
      const parsed = parseConditionList(
        groupRecord['conditions'],
        `Group ${i + 1}: `
      );
      if (!parsed.ok) {
        return { ok: false, error: parsed.error };
      }
      groups.push({
        combinator: readCombinator(groupRecord['combinator'], 'and'),
        conditions: parsed.conditions,
      });
    }
    return {
      ok: true,
      config: {
        combinator: readCombinator(record['combinator'], 'or'),
        groups,
      },
    };
  }

  // Multi-condition shape: a single AND group.
  const rawConditions = record['conditions'];
  if (rawConditions !== undefined && rawConditions !== null) {
    const parsed = parseConditionList(rawConditions, 'Filter node ');
    if (!parsed.ok) {
      return { ok: false, error: parsed.error };
    }
    return {
      ok: true,
      config: {
        combinator: 'or',
        groups: [{ combinator: 'and', conditions: parsed.conditions }],
      },
    };
  }

  // Legacy single-condition shape at the top level.
  const parsed = parseFilterCondition(record, 'Filter node ');
  if (!parsed.ok) {
    return { ok: false, error: parsed.error };
  }
  return {
    ok: true,
    config: {
      combinator: 'or',
      groups: [{ combinator: 'and', conditions: [parsed.condition] }],
    },
  };
}

function isMissing(fieldValue: unknown): boolean {
  return fieldValue === undefined || fieldValue === null;
}

/**
 * Coerces a value for ordering comparisons: a number stays numeric, a numeric
 * string parses to its number, and any other string that parses as a date
 * becomes its epoch-millisecond timestamp. Everything else is NaN (never orders).
 */
function toComparable(value: unknown): number {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') {
      return Number.NaN;
    }
    if (!Number.isNaN(Number(trimmed))) {
      return Number(trimmed);
    }
    const asDate = Date.parse(trimmed);
    if (!Number.isNaN(asDate)) {
      return asDate;
    }
  }
  return Number.NaN;
}

function compare(
  fieldValue: unknown,
  value: string | undefined,
  ok: (left: number, right: number) => boolean
): boolean {
  if (value === undefined) {
    return false;
  }
  const left = toComparable(fieldValue);
  const right = toComparable(value);
  return !Number.isNaN(left) && !Number.isNaN(right) && ok(left, right);
}

function looseEquals(fieldValue: unknown, value: string): boolean {
  if (typeof fieldValue === 'number') {
    const asNumber = Number(value);
    return !Number.isNaN(asNumber) && fieldValue === asNumber;
  }
  if (typeof fieldValue === 'boolean') {
    return String(fieldValue) === value;
  }
  if (typeof fieldValue === 'string') {
    return fieldValue === value;
  }
  if (isMissing(fieldValue)) {
    return false;
  }
  return JSON.stringify(fieldValue) === value;
}

/**
 * Substring/text search. A string matches by substring; an array matches if any
 * element matches; an object matches if the value appears anywhere in its JSON
 * text (so "the whole value contains X" works as a user expects); other scalars
 * are coerced to text. Case-sensitive — use the exact casing you want to find.
 */
function containsValue(fieldValue: unknown, value: string): boolean {
  if (fieldValue === undefined || fieldValue === null) {
    return false;
  }
  if (typeof fieldValue === 'string') {
    return fieldValue.includes(value);
  }
  if (Array.isArray(fieldValue)) {
    return fieldValue.some((element) => containsValue(element, value));
  }
  if (typeof fieldValue === 'object') {
    return JSON.stringify(fieldValue).includes(value);
  }
  return String(fieldValue).includes(value);
}

/**
 * Parses a list operator's value: a JSON array (`["web","email"]`) when it looks
 * like one, otherwise a comma-separated list (`web, email, api`). Items are
 * trimmed and blanks dropped so trailing commas are forgiving.
 */
export function parseListValue(value: string): string[] {
  const trimmed = value.trim();
  if (trimmed.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((item) =>
          typeof item === 'string' ? item : JSON.stringify(item)
        );
      }
    } catch {
      // Not valid JSON — fall through to comma-splitting.
    }
  }
  return trimmed
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item !== '');
}

/**
 * Evaluates a single filter condition against a resolved field value. Missing or
 * mismatched-type fields never throw — they resolve to a sensible boolean so the
 * workflow fails safely rather than crashing the run.
 */
export function evaluateFilterCondition(params: {
  fieldValue: unknown;
  operator: FilterOperator;
  value: string | undefined;
}): boolean {
  const { fieldValue, operator, value } = params;
  switch (operator) {
    case 'exists':
      return !isMissing(fieldValue);
    case 'not-exists':
      return isMissing(fieldValue);
    case 'equals':
      return value !== undefined && looseEquals(fieldValue, value);
    case 'not-equals':
      return value === undefined || !looseEquals(fieldValue, value);
    case 'contains':
      return value !== undefined && containsValue(fieldValue, value);
    case 'not-contains':
      return value === undefined || !containsValue(fieldValue, value);
    case 'greater-than':
      return compare(fieldValue, value, (left, right) => left > right);
    case 'less-than':
      return compare(fieldValue, value, (left, right) => left < right);
    case 'greater-than-or-equal':
      return compare(fieldValue, value, (left, right) => left >= right);
    case 'less-than-or-equal':
      return compare(fieldValue, value, (left, right) => left <= right);
    case 'one-of':
      return (
        value !== undefined &&
        parseListValue(value).some((item) => looseEquals(fieldValue, item))
      );
    case 'not-one-of':
      return (
        value === undefined ||
        !parseListValue(value).some((item) => looseEquals(fieldValue, item))
      );
    default:
      return Assert.unreachable(operator);
  }
}

/**
 * Evaluates a whole filter config against the upstream value. Each group combines
 * its conditions by the group's combinator (AND → all pass, OR → any passes), and
 * the groups are combined by the config's top-level combinator. {@link parseFilterConfig}
 * rejects empty configs/groups, so the vacuous-empty cases never happen in practice.
 */
/**
 * The top-level field names a filter reads, derived from its condition pointers.
 *
 * A pointer addresses a path (`/customer/email`); only its first segment names a
 * field that has to exist. Conditions with a blank path target the whole value
 * and name nothing. Callers use this to tell whether a saved filter still refers
 * to fields its source actually has.
 */
export function filterConfigFieldNames(config: FilterConfig): string[] {
  const names = new Set<string>();
  for (const group of config.groups) {
    for (const condition of group.conditions) {
      const pointer = filterFieldPointer(condition.path);
      if (pointer === '' || !pointer.startsWith('/')) {
        continue;
      }
      const [first] = pointer.slice(1).split('/');
      if (first !== undefined && first !== '') {
        // RFC 6901 escapes: `~1` is `/` and `~0` is `~`.
        names.add(first.replace(/~1/g, '/').replace(/~0/g, '~'));
      }
    }
  }
  return [...names];
}

export function evaluateFilterConfig(params: {
  upstream: unknown;
  config: FilterConfig;
  /** Ancestor outputs keyed by display name, for `NodeName/pointer` fields. */
  upstreamByNodeId?: Readonly<Record<string, unknown>>;
}): boolean {
  const evalCondition = (condition: FilterCondition): boolean =>
    evaluateFilterCondition({
      fieldValue: resolveUpstreamFieldValue({
        upstream: params.upstream,
        path: condition.path,
        upstreamByNodeId: params.upstreamByNodeId,
      }),
      operator: condition.operator,
      value: condition.value,
    });
  const groupPasses = (group: FilterGroup): boolean =>
    group.combinator === 'and'
      ? group.conditions.every(evalCondition)
      : group.conditions.some(evalCondition);
  return params.config.combinator === 'and'
    ? params.config.groups.every(groupPasses)
    : params.config.groups.some(groupPasses);
}
